import { useTranslation } from 'react-i18next';

import ApplianceCard from '../ApplianceCard';

import { getUnitType } from '~/UI/pages/OrderSummaryPage';
import { ProductType } from '~/core/enums';
import formatUOM from '~/core/helpers/formatUOM';
import { useSink } from '~/core/hooks/useSink';
import { ApplianceModel, PricedProduct } from '~/core/models';
import {
  getProductDescription,
  getSinkPrices,
  hasTrendingOrBestSelling,
  imgUrl,
  isBrowsedSink,
} from '~/core/utils';
import { ItemCountAdjustButtonProps } from '~atoms/ItemCountAdjustButton';
import DesignStepContainer from '~molecules/DesignStepContainer';
import RemoveApplianceModal from '~organisms/RemoveApplianceModal';

interface SuppliedSinkItemsProps {
  sinks: ApplianceModel[];
  onSinkClick: (sink: ApplianceModel, isDragAndDrop?: boolean) => void;
}
const SuppliedSinkItems = ({ sinks, onSinkClick }: SuppliedSinkItemsProps) => {
  const { t } = useTranslation();

  const {
    decreaseSinkQuantityHandler,
    getSinkCutoutLaborProductData,
    selectedSinkId,
    selectedSinks,
    setShowRemoveApplianceModal,
    showRemoveApplianceModal,
  } = useSink();

  return (
    <DesignStepContainer>
      {sinks.map((sink) => {
        const unitOfMeasure = formatUOM(
          getUnitType(sink as unknown as PricedProduct)
        );

        const { cutoutLaborProduct } = getSinkCutoutLaborProductData(sink);
        const { sinkAndCutoutLaborPriceWithDiscount } = getSinkPrices(
          sink,
          cutoutLaborProduct
        );

        const isSelectedSink = sink.productId === selectedSinkId;

        const dndArgs = {
          appliance: sink,
          onDragEnd: () => onSinkClick(sink, true),
        };

        const counterProps: ItemCountAdjustButtonProps = {
          quantity:
            selectedSinks[sink.productId] > 0
              ? selectedSinks[sink.productId]
              : undefined,
          onIncrease: () => onSinkClick(sink),
          onDecrease: decreaseSinkQuantityHandler(sink.productId),
        };

        return (
          <ApplianceCard
            applianceType={ProductType.SINK}
            counterProps={counterProps}
            discountPercentage={sink.discountPercentage || undefined}
            dndArgs={dndArgs}
            image={imgUrl(sink.beautyShotImageUrl || sink.imageUrl, 300)}
            isSelected={isSelectedSink}
            key={`sink-${sink.productId}`}
            onClick={() => onSinkClick(sink)}
            priceWithDiscount={sinkAndCutoutLaborPriceWithDiscount}
            priceWithoutDiscount={sinkAndCutoutLaborPriceWithDiscount}
            trendLabel={hasTrendingOrBestSelling(sink.features)}
            title={getProductDescription(sink)}
            tooltipText={t('DESIGN_TOOLBAR.DRAG_ONTO_DESIGN')}
            unitOfMeasure={unitOfMeasure}
            description={
              isBrowsedSink(sink)
                ? t('SCROLLABLE_AREA.SINK.ADDITIONAL_VALUES_INFORMATION')
                : t(
                    `SCROLLABLE_AREA.SINK.ADDITIONAL_VALUES_INFORMATION_CUSTOMER_SUPPLIED`
                  )
            }
          />
        );
      })}

      <RemoveApplianceModal
        isOpen={showRemoveApplianceModal}
        onClose={() => setShowRemoveApplianceModal(false)}
      />
    </DesignStepContainer>
  );
};

export default SuppliedSinkItems;
